import { supabase } from '@/lib/supabaseClient'
import { withAuthRetry } from '@/lib/authUtils'
import { log } from '@/lib/logger'

export interface ContactEmail {
  type: 'work' | 'personal' | 'other'
  email: string
  status?: string | null
}

export interface ContactPhone {
  type: 'work' | 'mobile' | 'other'
  number: string
  raw_number?: string | null
}

export interface CandidateData {
  candidate_name: string
  email?: string | null
  phone?: string | null
  contact_emails?: ContactEmail[] | null
  contact_phones?: ContactPhone[] | null
  location_country?: string | null
  location_state?: string | null
  location_city?: string | null
  salary_amount?: number | null
  salary_currency?: string | null
  salary_period?: string | null
  profile_summary?: string | null
  linkedin_url?: string | null
  resume_url?: string | null
  skills?: string[] | null
  status?: string
  source?: string
  /** Latest job title — canonical column. */
  current_job_title?: string | null
  /** Latest employer — canonical column. */
  company_current?: string | null
  /** Form-only aliases (candidate form + resume parser) mapped on write. */
  current_role?: string | null
  current_company?: string | null
  organization_id: string
  created_by: string
}


export interface ExistingCandidate {
  id: string
  candidate_name: string
  email: string | null
  linkedin_url: string | null
  [key: string]: any
}

export interface DuplicateCheckResult {
  isDuplicate: true
  existingCandidate: ExistingCandidate
  incomingData: Partial<CandidateData>
  mergedData: any
  /** strong: same Apollo id, LinkedIn or email; medium: same phone and name. */
  strength: 'strong' | 'medium'
  /** Why we think it's the same person ("Same email", ...). */
  reasons: string[]
  /** A strong match whose LinkedIn or Apollo id differs: always ask. */
  conflict: boolean
  /** Strong and no conflict: fill the existing record without asking. */
  autoMergeable: boolean
}

/**
 * Smart merge helper - prioritizes more complete information
 */
export const smartMerge = (existing: any, incoming: any): any => {
  const merged = { ...existing }
  
  Object.keys(incoming).forEach(key => {
    const existingValue = existing[key]
    const incomingValue = incoming[key]
    
    // Skip undefined or null incoming values
    if (incomingValue === undefined || incomingValue === null) return
    
    // If existing is null/empty, use incoming
    if (!existingValue || existingValue === '') {
      merged[key] = incomingValue
    } 
    // For arrays (like skills), merge and deduplicate
    else if (Array.isArray(incomingValue) && Array.isArray(existingValue)) {
      merged[key] = [...new Set([...existingValue, ...incomingValue])]
    }
    // For strings, prefer the longer value (more detail)
    else if (typeof incomingValue === 'string' && typeof existingValue === 'string') {
      if (incomingValue.length > existingValue.length) {
        merged[key] = incomingValue
      }
    }
    // For numbers, prefer the incoming value if it's greater
    else if (typeof incomingValue === 'number' && typeof existingValue === 'number') {
      if (incomingValue > existingValue) {
        merged[key] = incomingValue
      }
    }
    // For any other case, prefer incoming value if existing is falsy
    else if (!existingValue) {
      merged[key] = incomingValue
    }
  })
  
  return merged
}

/**
 * Who in the workspace this person already is (Find Phase 1, 2026-10-12).
 * Asks find_candidate_matches(), the same function Collect, applications
 * and the extension use: Apollo id, LinkedIn or email = strong; phone and
 * name = medium. Weak matches (name and company) aren't raised here.
 */
export async function checkForDuplicateCandidate(
  candidateData: Partial<CandidateData>,
  organizationId: string,
  tenantId?: string
): Promise<DuplicateCheckResult | null> {
  let tenant = tenantId
  if (!tenant) {
    const { data: org } = await supabase.from('organizations').select('tenant_id').eq('id', organizationId).maybeSingle()
    tenant = org?.tenant_id ?? undefined
  }
  if (!tenant) return null

  const payload = {
    candidate_name: candidateData.candidate_name ?? null,
    email: candidateData.email ?? null,
    phone: candidateData.phone ?? null,
    linkedin_url: candidateData.linkedin_url ?? null,
    company_current: candidateData.company_current ?? candidateData.current_company ?? null,
  }
  if (!payload.email && !payload.phone && !payload.linkedin_url) return null

  const result = await withAuthRetry(async () =>
    await supabase.rpc('find_candidate_matches', { p_tenant: tenant!, p_payload: payload })
  )
  if (result.error) {
    log.error('Error checking for duplicates:', result.error)
    return null
  }
  const match = (result.data || []).find((m: { strength: string }) => m.strength === 'strong' || m.strength === 'medium')
  if (!match) return null

  const { data: existing } = await supabase.from('candidates').select('*').eq('id', match.candidate_id).maybeSingle()
  if (!existing) return null

  log.debug('Duplicate candidate found:', match.candidate_id, match.reasons)
  return {
    isDuplicate: true,
    existingCandidate: existing as ExistingCandidate,
    incomingData: candidateData,
    mergedData: smartMerge(existing, candidateData),
    strength: match.strength as 'strong' | 'medium',
    reasons: match.reasons ?? [],
    conflict: !!match.conflict,
    autoMergeable: match.strength === 'strong' && !match.conflict,
  }
}

/**
 * A strong match with no conflict: fill what the existing record is missing
 * (its own values always win) and, when given, put them in the job. Goes
 * through merge-candidates, so it is audited like any merge.
 */
export async function autoMergeIntoExisting(
  existingCandidateId: string,
  incoming: Partial<CandidateData>,
  jobId?: string | null,
  stageId?: string | null,
  notes?: string | null,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('merge-candidates', {
    body: {
      action: 'merge',
      surviving_candidate_id: existingCandidateId,
      incoming,
      resolutions: {},
      job_id: jobId ?? null,
      stage_id: stageId ?? null,
      notes: notes ?? null,
    },
  })
  if (error) throw error
  if (data?.error) throw new Error(String(data.error))
  return (data?.candidate_id as string) ?? existingCandidateId
}

/**
 * Create a new candidate in the database
 */
export async function createCandidate(candidateData: CandidateData) {
  log.debug('Creating new candidate:', candidateData.candidate_name)

  // Fetch tenant_id from organization_id for defense-in-depth
  let tenantId: string | null = null
  if (candidateData.organization_id) {
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('tenant_id')
      .eq('id', candidateData.organization_id)
      .single()
    
    if (!orgError && orgData) {
      tenantId = orgData.tenant_id
    }
  }

  const { data: newCandidate, error: createError } = await withAuthRetry(async () =>
    await supabase
      .from('candidates')
      .insert([{
        candidate_name: candidateData.candidate_name,
        email: candidateData.email,
        phone: candidateData.phone,
        contact_emails: candidateData.contact_emails?.map(e => JSON.stringify(e)) || [],
        contact_phones: candidateData.contact_phones?.map(p => JSON.stringify(p)) || [],
        location_country: candidateData.location_country,
        location_state: candidateData.location_state,
        location_city: candidateData.location_city,
        salary_amount: candidateData.salary_amount,
        salary_currency: candidateData.salary_currency,
        salary_period: candidateData.salary_period,
        profile_summary: candidateData.profile_summary,
        linkedin_url: candidateData.linkedin_url,
        resume_url: candidateData.resume_url,
        skills: candidateData.skills,
        current_job_title: candidateData.current_job_title ?? candidateData.current_role ?? null,
        company_current: candidateData.company_current ?? candidateData.current_company ?? null,

        status: candidateData.status || 'active',
        source: candidateData.source || 'direct',
        created_by: candidateData.created_by,
        organization_id: candidateData.organization_id,
        tenant_id: tenantId,
      }])
      .select()
      .single()
  )

  if (createError) {
    log.error('Error creating candidate:', createError)
    
    // Handle unique constraint violation — return duplicate info instead of throwing
    if (createError.code === '23505') {
      log.debug('Unique constraint hit, finding existing candidate for merge dialog')
      const existing = await findExistingCandidateForMerge(candidateData, tenantId)
      if (existing) {
        return {
          isDuplicate: true,
          existingCandidate: existing,
          mergedData: smartMerge(existing, candidateData)
        }
      }
    }
    
    // Provide more helpful error messages for RLS violations
    if (createError.code === '42501') {
      throw new Error('Permission denied: You do not have the required permissions to create candidates in this organization. Please verify you are an active recruiter or admin.')
    }
    
    throw createError
  }

  log.debug('Created candidate:', newCandidate.id)
  return newCandidate
}

/**
 * Find an existing candidate by email or name+tenant for merge dialog after constraint violation
 */
async function findExistingCandidateForMerge(
  candidateData: CandidateData,
  tenantId: string | null
): Promise<ExistingCandidate | null> {
  try {
    let query = supabase.from('candidates').select('*')
    
    if (candidateData.email) {
      query = query.eq('email', candidateData.email)
    } else {
      query = query.eq('candidate_name', candidateData.candidate_name)
    }
    
    if (tenantId) {
      query = query.eq('tenant_id', tenantId)
    }
    
    const { data, error } = await query.limit(1)
    if (error || !data?.length) return null
    return data[0] as ExistingCandidate
  } catch {
    return null
  }
}

/**
 * Merge candidate data into an existing candidate
 */
export async function mergeCandidate(existingCandidateId: string, candidateData: Partial<CandidateData>) {
  log.debug('Merging candidate data:', { candidateId: existingCandidateId })

  const { data: existingCandidate, error: fetchError } = await withAuthRetry(async () =>
    await supabase
      .from('candidates')
      .select('*')
      .eq('id', existingCandidateId)
      .single()
  )

  if (fetchError || !existingCandidate) {
    log.error('Error fetching existing candidate:', fetchError)
    throw fetchError || new Error('Existing candidate not found')
  }

  const mergedData = smartMerge(existingCandidate, candidateData)

  // Only update columns that actually exist on public.candidates.
  // The incoming form payload may include form-only fields (e.g. current_company,
  // salary_amount_max, first_name, last_name, assignedJobId, assignedStageId, job_id, notes)
  // which would otherwise cause Postgres "column does not exist" errors.
  const allowedColumns = [
    'candidate_name',
    'email',
    'phone',
    'contact_emails',
    'contact_phones',
    'location_country',
    'location_state',
    'location_city',
    'salary_amount',
    'salary_currency',
    'salary_period',
    'profile_summary',
    'linkedin_url',
    'resume_url',
    'skills',
    'status',
    'source',
    'current_job_title',
    'company_current',
  ] as const


  const updateFields: Record<string, any> = {}
  for (const key of allowedColumns) {
    const value = (mergedData as any)[key]
    if (value !== undefined) updateFields[key] = value
  }
  // Map the form-only aliases onto their real columns when the canonical
  // fields were not supplied.
  if (updateFields.current_job_title === undefined && (mergedData as any).current_role) {
    updateFields.current_job_title = (mergedData as any).current_role
  }
  if (updateFields.company_current === undefined && (mergedData as any).current_company) {
    updateFields.company_current = (mergedData as any).current_company
  }


  const { data: updatedCandidate, error: updateError } = await withAuthRetry(async () =>
    await supabase
      .from('candidates')
      .update(updateFields)
      .eq('id', existingCandidateId)
      .select()
      .single()
  )

  if (updateError) {
    log.error('Error merging candidate:', updateError)
    throw updateError
  }

  log.debug('Merged candidate:', updatedCandidate.id)
  return updatedCandidate
}

/**
 * Create a job-candidate association
 */
export async function createJobAssociation(
  jobId: string,
  candidateId: string,
  notes?: string | null,
  stageId?: string | null,
  status: 'active' | 'rejected' | 'hired' | 'offer' = 'active',
  addedBy?: string
) {
  log.debug('Creating job association:', { jobId, candidateId })

  const { error: assocError } = await withAuthRetry(async () =>
    await supabase
      .from('job_candidate_associations')
      .insert([{
        job_id: jobId,
        candidate_id: candidateId,
        notes: notes || null,
        current_stage_id: stageId || null,
        status,
        added_by: addedBy
      }])
  )

  if (assocError) {
    log.error('Error creating job association:', assocError)
    throw assocError
  }

  log.debug('Created job association')
}

/**
 * Format candidate salary expectation as a short display string.
 * Examples: "$185k / yr", "€90k / yr", "$50 / hr".
 * Returns null when amount is missing.
 */
export function formatSalaryExpectation(c: {
  salary_amount?: number | string | null
  salary_currency?: string | null
  salary_period?: string | null
} | null | undefined): string | null {
  if (!c) return null
  const raw = c.salary_amount
  const amount = typeof raw === 'string' ? parseFloat(raw) : raw
  if (!amount || isNaN(amount as number)) return null

  const currency = (c.salary_currency || 'USD').toUpperCase()
  let symbol = currency
  try {
    const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
    const s = parts.find(p => p.type === 'currency')?.value
    if (s) symbol = s
  } catch { /* fallback to code */ }

  const periodRaw = (c.salary_period || 'year').toLowerCase()
  const periodSuffix = periodRaw.startsWith('hour') || periodRaw === 'hr'
    ? 'hr'
    : periodRaw.startsWith('month') || periodRaw === 'mo'
      ? 'mo'
      : periodRaw.startsWith('week') || periodRaw === 'wk'
        ? 'wk'
        : 'yr'

  const n = amount as number
  const display = n >= 1000
    ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1).replace(/\.0$/, '')}k`
    : `${n}`

  return `${symbol}${display} / ${periodSuffix}`
}
